import type {
  IReportColumn,
  ReportRowType,
} from '~/presentation/reports/common/Visualization/types';
import type { IReportHeaderSummary } from './WrapperHeader';
import type { ReportColumnLayoutMap } from '../../types';

import {
  getPreviousTotalColumnName,
  getTotalColumnName,
} from '~/presentation/reports/common/Visualization/utils';
import { getColumnLayoutStyles } from '../../utils';
import { useTranslation } from 'react-i18next';
import { SummaryCell } from './SummaryCell';
import { useReportData } from '../../hooks';

interface SummaryRowProps {
  columns: IReportColumn[];
  row?: ReportRowType;
  dimensions: string[];
  summary?: IReportHeaderSummary;
  columnLayoutMap: ReportColumnLayoutMap;
}

export const SummaryRow = ({
  columns,
  row,
  dimensions,
  summary,
  columnLayoutMap,
}: SummaryRowProps) => {
  const { t } = useTranslation();
  const { showPrevious, previousContent, content } = summary || {};
  const { formatData } = useReportData();

  if (summary?.hide || !dimensions.length) return null;

  const getCellValue = (columnName: string, index: number, formatter: string) => {
    const layout = columnLayoutMap[columnName];
    if (index === 0)
      return (
        <SummaryCell
          currentValue={content || t('Summary')}
          showPrevious={showPrevious}
          previousValue={previousContent}
          columnLayout={layout}
        />
      );

    const isDimensionCol = dimensions.includes(columnName);
    if (isDimensionCol) return '';

    const cellValue = row?.[getTotalColumnName(columnName)];
    const previousValue = row?.[getPreviousTotalColumnName(columnName)];
    const formattedValue = cellValue
      ? formatData({ value: cellValue, formatter, name: columnName })
      : cellValue;
    const formattedPreviousValue = previousValue
      ? formatData({ value: previousValue, formatter, name: columnName })
      : previousValue;

    if (typeof formattedValue === 'object') return '';

    return (
      <SummaryCell
        currentValue={formattedValue}
        showPrevious={showPrevious}
        previousValue={formattedPreviousValue}
        columnLayout={layout}
      />
    );
  };

  return (
    <div className="GReportTable__Summary-Row">
      {columns.map(({ name, dataType }, index) => (
        <div
          key={name}
          className="GReportTable__Summary-Cell"
          style={getColumnLayoutStyles(columnLayoutMap[name])}
        >
          {getCellValue(name, index, dataType)}
        </div>
      ))}
    </div>
  );
};
